/**
 * Formatting helpers.
 *
 * Money is formatted FROM THE STRING the API returns. numeric(18,6) does not
 * survive a round trip through a JS double and the loss is silent, so nothing
 * here ever calls parseFloat on an amount (doc 07 section 3).
 */
export function formatMoney(amount: string | null, currency: string | null): string {
  if (amount === null) return '—';
  const negative = amount.startsWith('-');
  const [whole = '0', frac = ''] = amount.replace('-', '').split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const cents = (frac + '00').slice(0, 2);
  const symbol = currency === 'ZAR' ? 'R' : currency ? currency + ' ' : '';
  return `${negative ? '-' : ''}${symbol}${grouped}.${cents}`;
}

export function formatDuration(seconds: number): string {
  if (seconds <= 0) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Timestamps render in the TENANT's timezone with an explicit label, never the
 * browser's. A South African admin looking at a tenant configured for Namibia
 * must see Namibian time or the call records are misleading (doc 07 section 3).
 */
export function formatDateTime(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-ZA', {
    dateStyle: 'medium', timeStyle: 'short', timeZone: timezone,
  }).format(new Date(iso));
}

export function formatTime(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-ZA', {
    timeStyle: 'medium', timeZone: timezone,
  }).format(new Date(iso));
}

export function timezoneAbbr(timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-ZA', { timeZone: timezone, timeZoneName: 'short' })
    .formatToParts(new Date());
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? timezone;
}

export const formatNumber = (n: number): string =>
  n.toLocaleString('en-ZA').replace(/,/g, ' ');

/**
 * Handles BOTH directions.
 *
 * It used to assume the past, so a future timestamp produced a negative
 * difference, fell through `mins < 1`, and rendered as "just now" -- which is
 * how a registration expiring in four minutes came to read as though it had
 * just expired.
 */
export function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const future = diff < 0;
  const mins = Math.round(Math.abs(diff) / 60000);
  const say = (v: string): string => (future ? `in ${v}` : `${v} ago`);

  if (mins < 1) {
    const secs = Math.round(Math.abs(diff) / 1000);
    if (secs < 10) return 'just now';
    return say(`${secs}s`);
  }
  if (mins < 60) return say(`${mins}m`);
  const hours = Math.round(mins / 60);
  if (hours < 24) return say(`${hours}h`);
  return say(`${Math.round(hours / 24)}d`);
}
